import {useReducer} from "react";
import {useTranslation} from "react-i18next";
import {flexRender} from "@tanstack/react-table";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

import Pagination from "./Pagination";
import PageEntriesSelector from "./PageEntriesSelector";
import RowColoringSelector from "./RowColoringSelector";
import SearchBox from "./SearchBox";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "materialDashboard/components/MDBox";
import MDTypography from "materialDashboard/components/MDTypography";

// Material Dashboard 2 React example components
import DataTableHeadCell from "materialDashboard/examples/Tables/DataTable/DataTableHeadCell";
import {styled} from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";


export const defaultColoringOptions = [
    {value: "none", getColor: () => "transparent"},
    {value: "alternate", getColor: (row, i) => i % 2 !== 0 ? "#f8f9fa" : "transparent"},
]

function DataTable({
                       dataTableId,
                       entriesPerPage,
                       canSearch,
                       filtersEl,
                       showTotalEntries,
                       table,
                       pagination,
                       isSorted,
                       noEndBorder,
                       showFilterInitialValue=false,
                       searchPlaceholder,
                       stickyHeader,
                       showRowColoring = false,
                       coloringOptions,
                       rowColoring,
                       setRowColoring
                   }) {
    const {t} = useTranslation(["components"]);

    // Detect if the single-axis-scroll-container is supported.
    const isSingleAxisScrollSupported = typeof CSS !== "undefined" &&
        CSS.supports &&
        CSS.supports("named-feature(single-axis-scroll-container)");

    // The sticky header is enabled only if the prop is true AND the feature is supported.
    const isStickyHeader = stickyHeader && isSingleAxisScrollSupported;

    const headerGroups = table.getHeaderGroups();
    const pageOptions = table.getPageOptions();

    const {pagination: {pageIndex, pageSize}} = table.getState()
    const {rows} = table.getRowModel()
    const sortingState = table.getState().sorting

    // Toggle filter state
    const [showFilter, toggleFilter] = useReducer(state => !state, showFilterInitialValue);

    // Setting the entries starting point
    const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

    // Setting the entries ending point
    let entriesEnd;

    if (pageIndex === 0) {
        entriesEnd = pageSize;
    } else if (pageIndex === pageOptions.length - 1) {
        entriesEnd = pageSize * pageIndex + rows.length;
    } else {
        entriesEnd = pageSize * (pageIndex + 1);
    }

    return (
        <MDBox>
            {(entriesPerPage || canSearch) &&
                <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                    {canSearch && <SearchBox table={table} placeholder={searchPlaceholder}/>}
                    {
                        filtersEl && <IconButton onClick={toggleFilter}><Icon>filter_list</Icon></IconButton>
                    }
                    <MDBox display="flex" alignItems="center" ml="auto">
                        {showRowColoring && (
                            <RowColoringSelector
                                rowColoring={rowColoring}
                                setRowColoring={setRowColoring}
                                dataTableId={dataTableId}
                                coloringOptions={coloringOptions}
                            />
                        )}
                        {entriesPerPage && <PageEntriesSelector table={table} entriesPerPage={entriesPerPage} dataTableId={dataTableId}/>}
                    </MDBox>
                </MDBox>
            }
            <Collapse in={showFilter} timeout="auto">
                <MDBox px={3} pb={3}>
                    {filtersEl}
                </MDBox>
            </Collapse>

            <MDBox px={3}>
                <TableContainer sx={{
                    boxShadow: "none",
                    // When the header is sticky, a max-height must be set on the TableContainer.
                    // When single-axis-scroll-container is supported, use an explicit overflow value.
                    ...(isStickyHeader && {overflow: "auto clip"})
                }}>
                    <Table stickyHeader={isStickyHeader} sx={{
                        "& tbody .MuiTableRow-root:hover": {
                            backgroundColor: "#e3f2fd !important"
                        },
                        "& .vertical-separator": {
                            borderRight: "1px solid #e0e0e0 !important",
                            paddingRight: "2px !important",
                            "& + th, & + td": {
                                paddingLeft: "3px !important"
                            }
                        },
                        ...(isStickyHeader && {
                            "& thead tr": {
                                position: "sticky",
                                // On Chrome v153+ (where single-axis-scroll-container is supported)
                                // the sticky header is offset by 90px.
                                top: "90px",
                                // A background is required, otherwise the scrolling body rows
                                // remain visible through the (transparent) sticky header.
                                backgroundColor: "#fff",
                                zIndex: 10
                            }
                        })
                    }}>
                        <DataTableHead headerGroups={headerGroups}
                                       isSorted={isSorted}
                                       sortingState={sortingState}/>
                        <DataTableBody rows={rows}
                                       headerGroups={headerGroups}
                                       t={t}
                                       noEndBorder={noEndBorder}
                                       rowColoring={rowColoring}
                                       coloringOptions={coloringOptions}/>
                    </Table>
                </TableContainer>
            </MDBox>

            <MDBox
                display="flex"
                flexDirection={{xs: "column", sm: "row"}}
                justifyContent="space-between"
                alignItems={{xs: "flex-start", sm: "center"}}
                p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
            >
                {showTotalEntries && (
                    <MDBox mb={{xs: 3, sm: 0}}>
                        <MDTypography variant="button" color="secondary" fontWeight="regular">
                            {t("dataTable.totalEntries", {
                                entriesStart,
                                entriesEnd,
                                totalEntries: pageOptions.length * pageSize
                            })}
                        </MDTypography>
                    </MDBox>
                )}
                {<Pagination table={table} pagination={pagination}/>}
            </MDBox>
        </MDBox>
    );
}

// the headerValues is in the props to rerender the component when the header value change
const DataTableHead = ({headerGroups, isSorted, sortingState}) => {
    // A function that sets the sorted value for the table
    const setSortedValue = (header) => {
        const sortStatus = sortingState.find((sort) => sort.id === header.id);
        const canSort = header.column.getCanSort()
        if (!canSort)
            return false;
        if (!isSorted)
            return false;

        let sortedValue;
        if (sortStatus) {
            sortedValue = sortStatus.desc ? "desc" : "asce";
        } else {
            sortedValue = "none";
        }

        return sortedValue;
    };

    return <MDBox component="thead">
        {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta || {}
                    return <DataTableHeadCell
                        key={header.id}
                        {...meta}
                        verticalSeparator={meta.verticalSeparator}
                        sorted={setSortedValue(header)}
                        onSortClick={header.column.getToggleSortingHandler()}
                    >
                        {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                        )}
                    </DataTableHeadCell>
                })}
            </TableRow>
        ))}
    </MDBox>
}

// the headerValues is in the props to rerender the component when the header value change
const DataTableBody = ({rows, headerGroups, t, noEndBorder, rowColoring, coloringOptions}) => {
    return <TableBody>
        {rows.map((row, i) => {
            let backgroundColor = "transparent";
            const currentOption = coloringOptions.find(opt => opt.value === rowColoring);
            if (currentOption && typeof currentOption.getColor === "function") {
                backgroundColor = currentOption.getColor(row, i, rows);
            }

            return (
                <TableRow key={row.id} sx={{backgroundColor}}>
                    {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta || {}
                        return <StyledTd
                            key={cell.id}
                            noBorder={noEndBorder && rows.length - 1 === i}
                            className={meta.verticalSeparator ? "vertical-separator" : ""}
                            {...meta}
                        >
                            {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                            )}
                        </StyledTd>
                    })}
                </TableRow>
            );
        })}
        {
            rows.length === 0 && (
                <TableRow>
                    <StyledTd colSpan={headerGroups[0].headers.length} align="center"
                              sx={{height: "15rem"}}>
                        <MDTypography variant="body2" color="secondary">
                            <Icon fontSize="large">inbox</Icon><br/>
                            {t("dataTable.noRecords")}
                        </MDTypography>
                    </StyledTd>
                </TableRow>
            )
        }
    </TableBody>
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
    entriesPerPage: {defaultValue: 15, entries: [10, 15, 20, 25, 50, 100, 250]},
    canSearch: false,
    filtersEl: null,
    showTotalEntries: true,
    pagination: {variant: "gradient", color: "info"},
    isSorted: true,
    noEndBorder: false,
    stickyHeader: false,
    showRowColoring: false,
    coloringOptions: defaultColoringOptions,
    rowColoring: "alternate"
};

// Typechecking props for the DataTable
DataTable.propTypes = {
    dataTableId: PropTypes.string,
    entriesPerPage: PropTypes.oneOfType([
        PropTypes.shape({
            defaultValue: PropTypes.number,
            entries: PropTypes.arrayOf(PropTypes.number),
        }),
        PropTypes.bool,
    ]),
    canSearch: PropTypes.bool,
    filtersEl: PropTypes.node,
    showTotalEntries: PropTypes.bool,
    table: PropTypes.any.isRequired,
    pagination: PropTypes.shape({
        variant: PropTypes.oneOf(["contained", "gradient"]),
        color: PropTypes.oneOf([
            "primary",
            "secondary",
            "info",
            "success",
            "warning",
            "error",
            "dark",
            "light",
            "white",
        ]),
    }),
    isSorted: PropTypes.bool,
    noEndBorder: PropTypes.bool,
    stickyHeader: PropTypes.bool,
    showRowColoring: PropTypes.bool,
    coloringOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string,
        getColor: PropTypes.func.isRequired,
    })),
    rowColoring: PropTypes.string,
    setRowColoring: PropTypes.func,
};


// Use a styled td for performance reasons. Rendering a component for each cell is too slow.
export const StyledTd = styled('td')(({theme, noBorder, px, py, align}) => ({
    boxSizing: "border-box",
    padding: `${theme.spacing(py ?? 1.5)} ${theme.spacing(px ?? 3)}`,
    borderBottom: noBorder ? "none" : `${theme.borders.borderWidth[1]} solid ${theme.palette.light.main}`,
    fontSize: theme.typography.size.sm,
    textAlign: align ?? "left",
    color: theme.palette.text.main,
}));

export default DataTable;
